import React from "react";
import {
  ActionPopover,
  ActionPopoverItem,
} from "carbon-react/lib/components/action-popover";
import { withRouter } from "react-router-dom";
import { useAgentApi } from "../../services/AgentApi";
import { useAppContext } from "../../contexts/AppContext";
import { agentStatuses, isAgentInvite } from "../../constants/model";

const UserPopOverContextMenu = ({
  agent,
  onAgentUpdated,
  onAgentRemoved,
  onOpenCommentsDialog,
  onOpenChangeRoleDialog,
}) => {
  const { deactivateAgent, revokeAgentInvite, activateAgent } = useAgentApi();
  const { agentEntitlement } = useAppContext();

  const dialogStates = {
    deactivateAgent: {
      title: `Deactivate Agent`,
      userHeading: `${agent.email}`,
      subTitle:
        "Deactivating this agent will disable their access and prevent further use of this application.",
      promptCaption: "Confirm",
      prompts: [
        "Before proceeding, please ensure that you've selected the correct agent.",
      ],
      buttonCaption: "Deactivate Agent",
      performAction: async (params) => {
        const result = await deactivateAgent(agent.id, params?.agentComments);
        if (!result.success)
          throw new Error(
            result.error?.description || "Failed to deactivate agent"
          );
        onAgentUpdated(result.data);
      },
      successToast: `Agent deactivated '${agent.email}'.`,
    },
    revokeAgentInvite: {
      title: `Revoke Agent Invite`,
      userHeading: `${agent.email}`,
      subTitle:
        "Revoking this agent's invite will prevent them from accessing this application in the future.",
      promptCaption: "Confirm",
      prompts: [
        "Before proceeding, please ensure that you've selected the correct agent.",
      ],
      buttonCaption: "Revoke Invite",
      performAction: async (params) => {
        const result = await revokeAgentInvite(agent.id, params?.agentComments);
        if (!result.success)
          throw new Error(
            result.error?.description || "Failed to revoke invitation"
          );
        onAgentRemoved(agent.id);
      },
      successToast: `Agent invite revoked '${agent.email}'.`,
    },
    reactivateAgent: {
      title: `Reactivate Agent`,
      userHeading: `${agent.email}`,
      subTitle:
        "Reactivating this agent will restore their access to this application.",
      promptCaption: "Confirm",
      prompts: [
        "Before proceeding, please ensure that you've selected the correct agent.",
      ],
      buttonCaption: "Reactivate Agent",
      performAction: async (params) => {
        const result = await activateAgent(agent.id, params?.agentComments);
        if (!result.success)
          throw new Error(
            result.error?.description || "Failed to reactivate agent"
          );
        onAgentUpdated(result.data);
      },
      successToast: `Agent reactivated '${agent.email}'.`,
    },
  };

  const isAgentAdministrable = (agent) => {
    return (
      agentEntitlement &&
      agentEntitlement.agents.administrableRoles.includes(agent.role)
    );
  };

  const canAdministerTwoOrMoreRoles = (agent) => {
    return (
      agentEntitlement && agentEntitlement.agents.administrableRoles.length > 2
    );
  };

  const getStateChangePopoverItemState = (agent) => {
    return agent && isAgentInvite(agent)
      ? {
          id: "agent-actions-menu-revoke-invite",
          icon: "delete",
          disabled: !isAgentAdministrable(agent),
          onClickAction: () =>
            onOpenCommentsDialog(dialogStates.revokeAgentInvite),
          caption: "Revoke Invite",
        }
      : agent.status === agentStatuses.active.value
      ? {
          id: "agent-actions-menu-deactivate",
          icon: "locked",
          disabled: !isAgentAdministrable(agent),
          onClickAction: () =>
            onOpenCommentsDialog(dialogStates.deactivateAgent),
          caption: "Deactivate",
        }
      : agent.status === agentStatuses.deactivated.value
      ? {
          id: "agent-actions-menu-reactivate",
          icon: "unlocked",
          disabled: !isAgentAdministrable(agent),
          onClickAction: () =>
            onOpenCommentsDialog(dialogStates.reactivateAgent),
          caption: "Reactivate",
        }
      : null;
  };

  const getChangeRollPopoverItemState = (agent) => {
    return agent && !isAgentInvite(agent)
      ? {
          icon: "people",
          disabled:
            !isAgentAdministrable(agent) || !canAdministerTwoOrMoreRoles(),
          onClickAction: () => onOpenChangeRoleDialog(agent),
          caption: "Change Role",
        }
      : null;
  };

  const getPopoverItemsState = () => {
    let items = [];
    const stateChangeItem = getStateChangePopoverItemState(agent);
    if (stateChangeItem) items.push(stateChangeItem);
    const changeRoleItem = getChangeRollPopoverItemState(agent);
    if (changeRoleItem) items.push(changeRoleItem);
    return items;
  };

  const popoverItemsState = getPopoverItemsState();

  return (
    <>
      {popoverItemsState && popoverItemsState.length > 0 ? (
        <ActionPopover id="agent-actions-menu">
          {popoverItemsState.map((popoverItemState) => {
            return (
              <ActionPopoverItem
                id={popoverItemState.id}
                key={popoverItemState.caption.replace(" ", "")}
                icon={popoverItemState.icon}
                disabled={popoverItemState.disabled}
                onClick={popoverItemState.onClickAction}
              >
                {popoverItemState.caption}
              </ActionPopoverItem>
            );
          })}
        </ActionPopover>
      ) : null}
    </>
  );
};

export default withRouter(UserPopOverContextMenu);
